import { EventSearchForm } from '@mark43/cad-api';
import { Box } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { SearchResults } from '../../search-windows/search-results';
import { EventSearchResultsHeader } from '../event-search-results-header';
import { EventSearchResultsTable } from '../event-search-results-table';
import { ErrorBoundary } from '../../monitoring/error-boundary';
import {
    EventSearchResultsPaginationProvider,
    useEventSearchResultsPaginationContext,
} from '../event-search-results-pagination-provider';
import { useSearchEventsQuery } from '../use-search-events-query';
import { useExecuteSavedEventSearchQuery } from '../use-execute-saved-event-search-query';
import { useObservableSearchResultSelectionStore } from '../../search-windows/use-observable-search-results-selection-store';
import { EventSearchSortProvider } from '../event-search-sort-provider';
import {
    SEARCH_RESULTS_EXPORTS_FOOTER_HEIGHT_PX,
    FR_TOTAL_NON_RESULTS_HEIGHT_PX,
} from '../../search-windows/constants';
import { SavedSearchDateRangeForm } from '../../search-windows/saved-search';
import { useEventSearchFileExport } from '../use-event-search-file-export';

interface EventSearchResultsProps {
    searchData: EventSearchForm;
    searchTab: number;
    setSavedSearchId: (savedSearchId?: number) => void;
    closeSearch: () => void;
    savedSearchId?: number;
    dateRangeOverride?: SavedSearchDateRangeForm;
}

const _EventSearchResults = observer(function _EventSearchResults({
    closeSearch,
    searchData,
    savedSearchId,
    setSavedSearchId,
    searchTab,
    dateRangeOverride,
}: EventSearchResultsProps) {
    const pagination = useEventSearchResultsPaginationContext();
    const searchEventsQuery = useSearchEventsQuery(searchTab, searchData, savedSearchId);
    const executeSavedSearchQuery = useExecuteSavedEventSearchQuery({
        tabId: searchTab,
        savedSearchId,
        dateRangeOverride,
    });
    const eventSearchResponse = searchEventsQuery.data ?? executeSavedSearchQuery.data;
    const totalCount = eventSearchResponse?.totalCount ?? 0;
    const results = eventSearchResponse?.eventSearchRows.map((row) => ({ id: row.eventId }));
    const selection = useObservableSearchResultSelectionStore(results ?? [], totalCount);
    const { isExportLoading, isCSVExportLoading, isConsolidatedExportAllowed, fileExportHandler } =
        useEventSearchFileExport(searchData);

    const showExportsFooter = results && selection.anySelected;
    const totalUsedPageHeight = showExportsFooter
        ? FR_TOTAL_NON_RESULTS_HEIGHT_PX + SEARCH_RESULTS_EXPORTS_FOOTER_HEIGHT_PX
        : FR_TOTAL_NON_RESULTS_HEIGHT_PX;
    const hasResults = (results?.length ?? 0) > 0;

    return (
        <ErrorBoundary
            name="SEARCH_RESULTS_TAB"
            actions={[{ children: 'Close tab', onClick: closeSearch }]}
        >
            <Box
                h={`calc(100% - ${totalUsedPageHeight}px)`}
                w="100%"
                overflowY="auto"
                style={{ marginTop: 0 }}
            >
                <SearchResults
                    isEventSearch
                    header={
                        <EventSearchResultsHeader
                            hasResults={hasResults}
                            eventSearchForm={searchData}
                            savedSearchId={savedSearchId}
                            setSavedSearchId={setSavedSearchId}
                        />
                    }
                    searchQuery={searchEventsQuery}
                    executeSavedSearchQuery={executeSavedSearchQuery}
                    results={results}
                    selection={selection}
                    pagination={pagination}
                    onFileExport={fileExportHandler}
                    isExportLoading={isExportLoading}
                    isCSVExportLoading={isCSVExportLoading}
                    allowConsolidatedExport={isConsolidatedExportAllowed}
                >
                    <EventSearchResultsTable
                        eventSearchResponse={eventSearchResponse}
                        selection={selection}
                        customStyle={{ backgroundColor: 'var(--arc-colors-surface-background)' }}
                    />
                </SearchResults>
            </Box>
        </ErrorBoundary>
    );
});

export function EventSearchResults(props: EventSearchResultsProps) {
    return (
        <EventSearchResultsPaginationProvider>
            <EventSearchSortProvider>
                <_EventSearchResults {...props} />
            </EventSearchSortProvider>
        </EventSearchResultsPaginationProvider>
    );
}
